/**
 * Amazon Cognito（amazon-cognito-identity-js）によるサイト内完結の認証。
 * VITE_COGNITO_CLIENT_ID / VITE_COGNITO_USER_POOL_ID が未設定ならモックモード
 * （バックエンド未接続のデモ）として振る舞い、この module は使われない。
 *
 * トークンは localStorage に保持し、期限切れ前に refresh_token で更新する。
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoRefreshToken,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { Role } from '../api/roles';

const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
const REGION = (import.meta.env.VITE_COGNITO_REGION as string | undefined) || 'ap-northeast-1';

export const authEnabled = Boolean(CLIENT_ID && USER_POOL_ID);

const TOKEN_KEY = 'vrma-hub:tokens';

export interface AuthUser {
  sub: string;
  email: string;
  /** 表示名（email のローカル部）。バックエンドの author と同じ規則。 */
  name: string;
  /** Cognito グループ 'admins' に所属していれば true。 */
  isAdmin: boolean;
  /** Cognito グループから算出したロール一覧（'user' は全員が持つ）。 */
  roles: Role[];
}

interface StoredTokens {
  idToken: string;
  /** MFA 設定系の SDK メソッド（associateSoftwareToken 等）が内部で参照するため保持する。 */
  accessToken: string;
  refreshToken?: string;
  /** epoch ms。これを過ぎたら refresh する。 */
  expiresAt: number;
}

/** コード配信先の情報（電話番号 / メールアドレスの一部が伏字で入る）。 */
export interface DeliveryDetails {
  destination?: string;
  medium?: string;
}

// authEnabled のときだけ生成し、モジュール内で使い回す。
// UserPoolId からもリージョンは推測されるが、VITE_COGNITO_REGION を明示的に endpoint へ反映する。
const userPool: CognitoUserPool | null = authEnabled
  ? new CognitoUserPool({
      UserPoolId: USER_POOL_ID!,
      ClientId: CLIENT_ID!,
      endpoint: `https://cognito-idp.${REGION}.amazonaws.com`,
    })
  : null;

function requirePool(): CognitoUserPool {
  if (!userPool) throw new Error('認証が有効になっていません。');
  return userPool;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split('.')[1];
  const bin = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function loadTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function saveTokens(t: StoredTokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(t));
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Unity 連携など、トークンをそのまま外部（ローカルの他プロセス）へ渡す用途向けに、保存済みトークンを返す。未ログインなら null。 */
export function getStoredTokens(): { idToken: string; refreshToken?: string } | null {
  const tokens = loadTokens();
  if (!tokens) return null;
  return { idToken: tokens.idToken, refreshToken: tokens.refreshToken };
}

/** CognitoUserSession からトークンを取り出し localStorage に保存する。 */
function persistSession(session: CognitoUserSession): StoredTokens {
  const idToken = session.getIdToken();
  const accessToken = session.getAccessToken();
  const refreshToken = session.getRefreshToken();
  const exp = typeof idToken.payload.exp === 'number' ? idToken.payload.exp : 0;
  const tokens: StoredTokens = {
    idToken: idToken.getJwtToken(),
    accessToken: accessToken.getJwtToken(),
    refreshToken: refreshToken?.getToken(),
    expiresAt: exp * 1000,
  };
  saveTokens(tokens);
  return tokens;
}

/**
 * ログイン中ユーザーの CognitoUser インスタンスを、保存済みトークンからセッション込みで
 * 再構築する。amazon-cognito-identity-js の MFA 設定系メソッド
 * （associateSoftwareToken / verifySoftwareToken / setUserMfaPreference / getUserData）は
 * 内部で `signInUserSession.getAccessToken()` を参照するため、
 * このアプリが独自に localStorage 管理している idToken/accessToken/refreshToken から
 * CognitoUserSession を組み立てて渡す（SDK 自身のストレージ機構は使っていないため、
 * `CognitoUserPool.getCurrentUser()` は使えない）。
 */
function getAuthenticatedCognitoUser(): CognitoUser {
  const pool = requirePool();
  const tokens = loadTokens();
  if (!tokens) throw new Error('ログインしていません。');
  if (!tokens.accessToken) throw new Error('セッション情報が古いため、再ログインしてください。');
  const email = userFromToken(tokens.idToken).email;
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
    RefreshToken: tokens.refreshToken ? new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }) : undefined,
  });
  cognitoUser.setSignInUserSession(session);
  return cognitoUser;
}

function toDeliveryDetails(
  details: { Destination?: string; DeliveryMedium?: string } | undefined,
): DeliveryDetails | undefined {
  if (!details) return undefined;
  return { destination: details.Destination, medium: details.DeliveryMedium };
}

/** ログイン時に MFA コードの入力が必要になった状態。Login.tsx はこれを見てコード入力ステップへ進む。 */
export interface MfaChallenge {
  /**
   * 要求された MFA の種類。運用上有効化しているのは SOFTWARE_TOKEN_MFA（認証アプリ）のみで、
   * SMS_MFA は Cognito 側で無効化しているため通常は発生しない
   * （型上は amazon-cognito-identity-js の mfaRequired コールバックに対応させるために残している）。
   */
  challenge: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA';
  /** 認証アプリ等に表示された6桁コードで認証を完了し、成功すればセッションを保存してユーザーを返す。 */
  submitMfaCode: (code: string) => Promise<AuthUser>;
}

export type SignInResult = AuthUser | MfaChallenge;

/** signIn() の戻り値が MFA チャレンジかどうかを判定する（AuthUser には `challenge` プロパティが無い）。 */
export function isMfaChallenge(result: SignInResult): result is MfaChallenge {
  return typeof result === 'object' && result !== null && 'challenge' in result;
}

/**
 * メールアドレス + パスワードでサインインする（SRP）。
 * MFA 未登録のユーザーは cognitoUser.authenticateUser が直接 onSuccess を呼ぶため、
 * 以下の totpRequired/mfaRequired コールバックを経由せずそのまま AuthUser を返す
 * （= 既存ユーザーのログインは変わらない）。MFA 登録済みのユーザーだけ MfaChallenge が返る。
 *
 * captchaToken（Cloudflare Turnstile）は Cognito の PreAuthentication Lambda トリガーで
 * 検証される。amazon-cognito-identity-js の authenticateUser はコールバック引数を1つしか
 * 取らず、Cognito へ送る ClientMetadata は AuthenticationDetails の ValidationData /
 * ClientMetadata（どちらも Object.keys().length !== 0 の方が優先され、SRP の
 * InitiateAuth/RespondToAuthChallenge の両方に載る）から組み立てられる。PreAuth 側で
 * どちらの event フィールド（validationData / clientMetadata）に届くか不確実なため、
 * 両方に captchaToken を入れて送る。未指定（サイトキー未設定によるフォールバック）なら
 * 何も送らず、PreAuth 側もトークン無し＝未設定として通す。
 */
export async function signIn(email: string, password: string, captchaToken?: string): Promise<SignInResult> {
  const pool = requirePool();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
      ...(captchaToken
        ? { ValidationData: { captchaToken }, ClientMetadata: { captchaToken } }
        : {}),
    });

    const submitMfaCode = (mfaType: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA') => (code: string) =>
      new Promise<AuthUser>((res, rej) => {
        cognitoUser.sendMFACode(
          code,
          {
            onSuccess: (session) => {
              const tokens = persistSession(session);
              res(userFromToken(tokens.idToken));
            },
            onFailure: (err) => rej(err),
          },
          mfaType,
        );
      });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const tokens = persistSession(session);
        resolve(userFromToken(tokens.idToken));
      },
      onFailure: (err) => reject(err),
      // TOTP（認証アプリ）による MFA チャレンジ。運用上有効化しているのはこちらのみ。
      totpRequired: () => {
        resolve({ challenge: 'SOFTWARE_TOKEN_MFA', submitMfaCode: submitMfaCode('SOFTWARE_TOKEN_MFA') });
      },
      // SMS MFA は Cognito 側で有効化していないため通常は発火しない。念のため用意。
      mfaRequired: () => {
        resolve({ challenge: 'SMS_MFA', submitMfaCode: submitMfaCode('SMS_MFA') });
      },
    });
  });
}

/** 新規登録する。成功すると確認コードがメールで送られる。 */
export async function signUp(
  email: string,
  password: string,
): Promise<{ userConfirmed: boolean; deliveryDetails?: DeliveryDetails }> {
  const pool = requirePool();
  return new Promise((resolve, reject) => {
    const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email })];
    pool.signUp(email, password, attributes, [], (err, result) => {
      if (err || !result) {
        reject(err ?? new Error('サインアップに失敗しました。'));
        return;
      }
      resolve({
        userConfirmed: result.userConfirmed,
        deliveryDetails: toDeliveryDetails(result.codeDeliveryDetails),
      });
    });
  });
}

/** メール確認コードを確定する。 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  const pool = requirePool();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.confirmRegistration(code, false, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** メール確認コードを再送する。 */
export async function resendConfirmationCode(email: string): Promise<void> {
  const pool = requirePool();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.resendConfirmationCode((err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** パスワード再設定を開始する（確認コードを送信）。 */
export async function forgotPassword(
  email: string,
): Promise<{ deliveryDetails?: DeliveryDetails }> {
  const pool = requirePool();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        resolve({ deliveryDetails: toDeliveryDetails(data?.CodeDeliveryDetails) });
      },
      onFailure: (err) => reject(err),
    });
  });
}

/** 確認コード + 新パスワードでパスワード再設定を確定する。 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const pool = requirePool();
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** ローカルのトークンを破棄する（Hosted UI へのリダイレクトはしない）。 */
export function signOut(): void {
  clearTokens();
}

/** 有効な ID トークンを返す（必要なら refresh）。未ログインなら null。 */
export async function getIdToken(): Promise<string | null> {
  const tokens = loadTokens();
  if (!tokens) return null;
  if (Date.now() < tokens.expiresAt - 60_000) return tokens.idToken;
  if (!tokens.refreshToken || !userPool) {
    clearTokens();
    return null;
  }
  const pool = userPool;
  const email = userFromToken(tokens.idToken).email;
  if (!email) {
    clearTokens();
    return null;
  }
  const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.refreshSession(refreshToken, (err, session) => {
      if (err || !session) {
        clearTokens();
        resolve(null);
        return;
      }
      resolve(persistSession(session).idToken);
    });
  });
}

// ---- TOTP MFA（認証アプリ）の登録・状態確認・解除 --------------------------

const MFA_ISSUER = 'VRMA Hub';

/** 認証アプリ（Google Authenticator 等）に登録するための otpauth:// URI を組み立てる。 */
function otpauthUri(email: string, secretCode: string): string {
  const label = `VRMA%20Hub:${encodeURIComponent(email)}`;
  return `otpauth://totp/${label}?secret=${secretCode}&issuer=VRMA%20Hub`;
}

/** MFA 登録開始で得られるシークレット。QR コード化・手動入力の両方に使う。 */
export interface MfaEnrollment {
  secretCode: string;
  otpauthUri: string;
}

/** TOTP MFA の登録を開始する（シークレットを発行するだけで、まだ有効化はされない）。 */
export async function beginMfaSetup(): Promise<MfaEnrollment> {
  const cognitoUser = getAuthenticatedCognitoUser();
  const tokens = loadTokens();
  const email = tokens ? userFromToken(tokens.idToken).email : '';
  return new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      associateSecretCode: (secretCode) => {
        resolve({ secretCode, otpauthUri: otpauthUri(email, secretCode) });
      },
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * 認証アプリに表示された6桁コードで登録を確定し、SOFTWARE_TOKEN_MFA を
 * preferred な MFA として有効化する。beginMfaSetup() の後に呼ぶ。
 */
export async function confirmMfaSetup(code: string): Promise<void> {
  const cognitoUser = getAuthenticatedCognitoUser();
  await new Promise<void>((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, MFA_ISSUER, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
  await new Promise<void>((resolve, reject) => {
    cognitoUser.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** 現在ログイン中のユーザーの MFA 登録状況。 */
export interface MfaStatus {
  /** SOFTWARE_TOKEN_MFA が有効なユーザー設定に含まれているか。 */
  enabled: boolean;
  /** SOFTWARE_TOKEN_MFA が preferred（ログイン時に要求される）MFA になっているか。 */
  preferred: boolean;
}

/** ユーザーの現在の MFA 設定を取得する。 */
export async function getMfaStatus(): Promise<MfaStatus> {
  const cognitoUser = getAuthenticatedCognitoUser();
  return new Promise((resolve, reject) => {
    cognitoUser.getUserData((err, data) => {
      if (err || !data) {
        reject(err ?? new Error('MFA 設定の取得に失敗しました。'));
        return;
      }
      const settings = data.UserMFASettingList ?? [];
      resolve({
        enabled: settings.includes('SOFTWARE_TOKEN_MFA'),
        preferred: data.PreferredMfaSetting === 'SOFTWARE_TOKEN_MFA',
      });
    });
  });
}

/** TOTP MFA を無効化する。 */
export async function disableMfa(): Promise<void> {
  const cognitoUser = getAuthenticatedCognitoUser();
  await new Promise<void>((resolve, reject) => {
    cognitoUser.setUserMfaPreference(null, { PreferredMfa: false, Enabled: false }, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

// backend/src/lib/roles.ts の ROLE_COGNITO_GROUP と対応（'user' は全員が暗黙的に持つ）。
function rolesFromGroups(groups: string[]): Role[] {
  return groups.includes('admins') ? ['admin', 'user'] : ['user'];
}

/** 保存済みトークンからユーザー情報を取り出す。 */
export function userFromToken(idToken: string): AuthUser {
  const payload = decodeJwtPayload(idToken);
  const email = typeof payload.email === 'string' ? payload.email : '';
  const groups = Array.isArray(payload['cognito:groups'])
    ? (payload['cognito:groups'] as unknown[]).map(String)
    : [];
  const roles = rolesFromGroups(groups);
  return {
    sub: String(payload.sub),
    email,
    name: email.split('@')[0] || 'user',
    isAdmin: roles.includes('admin'),
    roles,
  };
}

const ERROR_MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'メールアドレスまたはパスワードが正しくありません。',
  UserNotConfirmedException: 'メール確認が未完了です。届いた確認コードを入力してください。',
  UsernameExistsException: 'このメールアドレスは既に登録されています。',
  CodeMismatchException: '確認コードが正しくありません。',
  ExpiredCodeException: '確認コードの有効期限が切れています。再送してください。',
  InvalidPasswordException: 'パスワードは 8 文字以上で入力してください。',
  InvalidParameterException: '入力内容に問題があります。もう一度ご確認ください。',
  LimitExceededException: '試行回数が上限に達しました。しばらくしてからお試しください。',
  TooManyRequestsException: '試行回数が上限に達しました。しばらくしてからお試しください。',
  // PreventUserExistenceErrors が ON のため通常は NotAuthorizedException 側に丸められるが、念のため。
  UserNotFoundException: 'メールアドレスまたはパスワードが正しくありません。',
};

/** Cognito のエラーを日本語メッセージへ変換する。該当が無ければ元のメッセージを返す。 */
export function translateCognitoError(err: unknown): string {
  const e = err as { code?: string; name?: string; message?: string } | null | undefined;
  const code = e?.code ?? e?.name;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return e?.message || String(err);
}
